import moment from 'moment';
import 'moment/locale/pt-br';

import type { BankSlipItem } from './types';

moment.locale('pt-br');

export type InvoiceFilterValues = {
  invoiceNumber: string;
  emissionMonth: string;
  amount: string;
};

export const EMISSION_HISTORY_MONTHS = 120;

const DATE_INPUT_FORMATS = ['YYYY-MM-DD', 'DD-MM-YYYY'];

export const getEmissionMonthLabel = (value: string): string => {
  const parsed = moment(value, 'YYYY/MM', true);
  if (!parsed.isValid()) return value;

  const label = parsed.format('MMM/YYYY');
  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const getEmissionMonthOptions = (
  selectedMonth = ''
): Array<{ value: string; label: string }> => {
  const options = Array.from({ length: EMISSION_HISTORY_MONTHS }, (_, index) => {
    const month = moment().subtract(index, 'months');
    const value = month.format('YYYY/MM');

    return {
      value,
      label: getEmissionMonthLabel(value),
    };
  });

  if (selectedMonth && !options.some((option) => option.value === selectedMonth)) {
    options.push({
      value: selectedMonth,
      label: getEmissionMonthLabel(selectedMonth),
    });
    options.sort((first, second) => second.value.localeCompare(first.value));
  }

  return options;
};

export const formatAmountInput = (value: string): string => {
  const digits = value.replace(/\D/g, '');

  if (!digits) return '';

  const number = Number(digits) / 100;
  if (number === 0) return '';

  return number.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatAmountFromApi = (value: string): string => {
  const normalized = value.replace(/\./g, '').replace(',', '.').trim();
  if (!normalized) return '';

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return '';

  return parsed.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const parseAmountToApi = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : null;
  }

  const normalized = trimmed.replace(/\./g, '').replace(',', '.');
  if (!normalized) return null;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : null;
};

export const isAmountReadyToApply = (value: string): boolean => {
  const parsed = parseAmountToApi(value);
  return parsed !== null && Number(parsed) > 0;
};

export const shouldClearAmountFilter = (value: string): boolean => {
  if (!value.trim()) return true;

  const parsed = parseAmountToApi(value);
  return parsed === null || Number(parsed) === 0;
};

export const getAmountFilterRangeForApi = (
  value: string
): { beginAmount: string; endAmount: string } | null => {
  const parsed = parseAmountToApi(value);
  if (!parsed) return null;

  const amount = Number(parsed);
  if (!Number.isFinite(amount) || amount <= 0) return null;

  const formatted = amount.toFixed(2);

  return {
    beginAmount: formatted,
    endAmount: formatted,
  };
};

const normalizeDateForApi = (value: string | null): string | null => {
  if (!value) return null;

  const parsed = moment(value, DATE_INPUT_FORMATS, true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : value;
};

const inferEmissionMonthFromDateRange = (
  beginDate: string | null,
  endDate: string | null
): string => {
  if (!beginDate || !endDate) return '';

  const begin = moment(beginDate, DATE_INPUT_FORMATS, true);
  const end = moment(endDate, DATE_INPUT_FORMATS, true);

  if (!begin.isValid() || !end.isValid()) return '';
  if (!begin.isSame(end, 'month')) return '';

  const monthStart = begin.clone().startOf('month');
  const monthEnd = begin.clone().endOf('month');

  if (!begin.isSame(monthStart, 'day') || !end.isSame(monthEnd, 'day')) {
    return '';
  }

  return begin.format('YYYY/MM');
};

const readAmountFromSearch = (params: URLSearchParams): string => {
  const amount = params.get('amount');
  if (amount) return amount;

  const beginAmount = params.get('begin-amount');
  if (beginAmount) {
    return formatAmountFromApi(beginAmount);
  }

  return '';
};

export const readFiltersFromSearch = (search: string): InvoiceFilterValues => {
  const params = new URLSearchParams(search);
  const emissionMonth =
    params.get('emission-month') ??
    inferEmissionMonthFromDateRange(
      params.get('begin-date'),
      params.get('end-date')
    );

  return {
    invoiceNumber: params.get('invoice-number') ?? '',
    emissionMonth,
    amount: readAmountFromSearch(params),
  };
};

export const readApiFiltersFromSearch = (search: string) => {
  const params = new URLSearchParams(search);
  const emissionMonth = params.get('emission-month');
  let beginDate = params.get('begin-date');
  let endDate = params.get('end-date');

  if (emissionMonth) {
    const parsed = moment(emissionMonth, 'YYYY/MM', true);
    if (parsed.isValid()) {
      beginDate = parsed.startOf('month').format('YYYY-MM-DD');
      endDate = parsed.endOf('month').format('YYYY-MM-DD');
    }
  }

  const amountFromDisplay = getAmountFilterRangeForApi(params.get('amount') ?? '');
  let beginAmount = params.get('begin-amount');
  let endAmount = params.get('end-amount') ?? beginAmount;

  if (amountFromDisplay) {
    beginAmount = amountFromDisplay.beginAmount;
    endAmount = amountFromDisplay.endAmount;
  } else if (beginAmount) {
    beginAmount = parseAmountToApi(beginAmount) ?? beginAmount;
    endAmount = parseAmountToApi(endAmount ?? beginAmount) ?? beginAmount;
  } else {
    beginAmount = null;
    endAmount = null;
  }

  return {
    invoiceNumber: params.get('invoice-number'),
    beginDate: normalizeDateForApi(beginDate),
    endDate: normalizeDateForApi(endDate),
    beginAmount,
    endAmount,
  };
};

export const hasActiveFilters = (filters: InvoiceFilterValues): boolean =>
  Boolean(filters.invoiceNumber || filters.emissionMonth || filters.amount);

export const hasActiveFiltersInSearch = (search: string): boolean => {
  const params = new URLSearchParams(search);

  return Boolean(
    params.get('invoice-number') ||
      params.get('emission-month') ||
      params.get('amount') ||
      params.get('begin-amount') ||
      (params.get('begin-date') && params.get('end-date'))
  );
};

export const buildSearchFromFilters = (values: InvoiceFilterValues): string => {
  const params = new URLSearchParams();

  if (values.invoiceNumber) {
    params.set('invoice-number', values.invoiceNumber);
  }

  if (values.emissionMonth) {
    params.set('emission-month', values.emissionMonth);
  }

  const apiAmount = getAmountFilterRangeForApi(values.amount);
  if (apiAmount) {
    params.set('amount', values.amount);
    params.set('begin-amount', apiAmount.beginAmount);
    params.set('end-amount', apiAmount.endAmount);
  }

  return params.toString();
};

export const isSameAmountFilter = (
  inputAmount: string,
  appliedAmount: string
): boolean => parseAmountToApi(inputAmount) === parseAmountToApi(appliedAmount);

export const matchesInvoiceAmount = (
  installment: { amountOriginal?: number | string },
  amount: string
): boolean => {
  const target = parseAmountToApi(amount);
  if (!target || installment.amountOriginal == null) return false;

  const installmentAmount = Number(installment.amountOriginal);
  const filterAmount = Number(target);

  if (!Number.isFinite(installmentAmount) || !Number.isFinite(filterAmount)) {
    return false;
  }

  return Math.abs(installmentAmount - filterAmount) < 0.01;
};

export const filterRowsByAmount = (
  rows: BankSlipItem[],
  amount: string
): BankSlipItem[] =>
  rows.filter(({ installment }) => matchesInvoiceAmount(installment, amount));

export const withoutAmountApiFilters = (
  filters: {
    beginDate: string | null;
    endDate: string | null;
    invoiceNumber: string | null;
    beginAmount: string | null;
    endAmount: string | null;
  }
) => ({
  ...filters,
  beginAmount: null,
  endAmount: null,
});
